/**
 * "DID YOU REQUEST ACCESS?" — the body, once. Desktop wraps it in a `Modal`,
 * the phone in a `Sheet`, exactly like `OwnerConfirm.tsx`: the chrome differs,
 * the question does not.
 *
 * Raised by the four primary CTAs that hand off to a signup page (the two
 * Playtests cards, the two detail overlays) at the moment they open the tab.
 * `selectors.3e#ptAskVM` carries the whole bag and the argument for why this
 * exists at all; the short version is that Steam publishes libraries, not
 * request queues, so a request is only ever knowable from the member.
 *
 * THE CARD IS REPEATED IN FULL on purpose. The answer can arrive minutes later,
 * after a tab switch, with several playtests open — a bare "did you request
 * access?" over a dimmed screen is not answerable in that state.
 *
 * `NOT YET` is not a refusal and must not read as one: it closes and writes
 * nothing, and the same button is waiting the next time the card is opened.
 */

import { useQd } from '../store/index.ts';
import { ptAskVM } from '../store/selectors.3e.ts';
import { PtHeroArt } from './PtArt.tsx';
import type { PtAskVM } from '../store/selectors.3e.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

export interface PtAskMetrics {
  /** hero height — 132 on the phone sheet, 148 in the desktop modal */
  hero: number;
  pad: string;
  gap: number;
}

/** Null when nothing is being asked about — both callers mount on `s.ptAsk`. */
export function usePtAsk(): PtAskVM | null {
  return ptAskVM(useQd());
}

export function PtAskBody({ v, m }: { v: PtAskVM; m: PtAskMetrics }): JSX.Element {
  const s = useQd();

  const btn = (bd: string, bg: string, clr: string) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid ' + bd,
    background: bg,
    color: clr,
    fontFamily: MONO,
    fontSize: '10.5px',
    letterSpacing: '.03em',
    height: 44,
    cursor: 'pointer',
    boxSizing: 'border-box' as const,
    textAlign: 'center' as const,
  });

  return (
    <>
      {/* the card, so the question is answerable on its own */}
      <div style={{ position: 'relative', height: m.hero, flex: 'none', background: v.heroBg }}>
        <div style={{ position: 'absolute', inset: 0, background: 'var(--gt-checker)' }} />
        <PtHeroArt id={v.id} cands={v.cands} ini={v.ini} fs={20} />
        <div
          style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,transparent 30%,rgba(5,5,8,.92))' }}
        />
        <div
          style={{ position: 'absolute', left: 14, bottom: 12, right: 14, display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          <div style={{ fontFamily: PIXEL, fontSize: 11, color: 'var(--gt-text)', lineHeight: 1.5 }}>{v.name}</div>
          <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-text-3)' }}>{v.dev}</div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: m.gap, padding: m.pad }}>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {v.chips.map((ch) => (
            <span
              key={ch.key}
              style={{
                fontFamily: MONO,
                fontSize: '8.5px',
                letterSpacing: '.06em',
                border: '1px solid var(--gt-border-2)',
                color: 'var(--gt-text-5)',
                padding: '3px 6px',
              }}
            >
              {ch.t}
            </span>
          ))}
        </div>

        <div style={{ fontFamily: MONO, fontSize: 10, lineHeight: 1.7, color: 'var(--gt-text-4)' }}>{v.body}</div>
        <div style={{ fontFamily: MONO, fontSize: '8.5px', lineHeight: 1.7, color: 'var(--gt-muted-2)' }}>{v.why}</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div
            onClick={() => {
              void s.ptReport(v.id, 'req');
              s.closePtAsk();
            }}
            style={btn('var(--acc)', 'var(--acc)', 'var(--gt-ink)')}
          >
            {v.yesTxt}
          </div>
          <div onClick={s.closePtAsk} style={btn('var(--gt-border-3)', 'transparent', 'var(--gt-text-3)')}>
            {v.noTxt}
          </div>
        </div>

        {/* The tab is easy to lose on a phone, and an unanswerable question is
            worse than no question — so the way back is in the dialog. */}
        {v.hasLink ? (
          <a
            href={v.linkHref}
            target="_blank"
            rel="noreferrer"
            style={{
              fontFamily: MONO,
              fontSize: '9.5px',
              color: 'var(--gt-text-5)',
              textDecoration: 'none',
              textAlign: 'center',
              padding: '10px 4px',
            }}
          >
            {v.linkTxt}
          </a>
        ) : null}
      </div>
    </>
  );
}
